/* The rail is a recessed column beside the list, not a card floating next to it: the sunken
   surface plus a single dividing edge is what makes the table read as the subject of the screen
   and the filters as its margin. */
:host {
  display: block;
  flex: 0 0 var(--agwa-filter-rail-width, 12.5rem);
  width: var(--agwa-filter-rail-width, 12.5rem);
  box-sizing: border-box;
  padding: 0.5rem 0.6rem 0.75rem;
  border-right: 1px solid var(--agwa-border);
  background: var(--agwa-surface-sunken);
  overflow-y: auto;
}

/* Folded to its strip: one icon wide, so the table gets the rail's width back. Scrolling is off
   because the strip holds nothing that scrolls, and padding goes so the strip button can be the
   whole surface. */
:host([rail-hidden]) {
  flex-basis: 2rem;
  width: 2rem;
  padding: 0;
  overflow: hidden;
}

.rail-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.25rem;
  margin-bottom: 0.35rem;
}

.rail-actions {
  display: flex;
  align-items: center;
  gap: 0.25rem;
}

/* The hide control and the strip's show control are plain icon buttons in the rail's own quiet
   palette - a Shoelace button here would out-weigh the heading it sits beside. */
.hide,
.strip {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.15rem;
  font: inherit;
  color: var(--agwa-text-subtle);
  background: none;
  border: 0;
  border-radius: 3px;
  cursor: pointer;
}

.hide:hover,
.strip:hover {
  color: var(--agwa-text);
  background: var(--agwa-surface-hover);
}

.hide:focus-visible,
.strip:focus-visible {
  outline: 2px solid var(--agwa-accent);
  outline-offset: -2px;
}

/* The strip is the entire folded rail: full height, so the way back is wherever the reader
   clicks, with the funnel at the top where the rail's heading was and the applied count under it. */
.strip {
  flex-direction: column;
  justify-content: flex-start;
  gap: 0.35rem;
  width: 100%;
  height: 100%;
  min-height: 6rem;
  padding-top: 0.6rem;
  border-radius: 0;
}

.strip sl-icon {
  font-size: 0.875rem;
}

.rail-title {
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--agwa-text-subtle);
}

/* Shoelace's text button carries form-control padding it does not need when it is acting as a
   link inside a heading row. */
.rail-head sl-button::part(base) {
  padding: 0;
  font-size: 0.75rem;
}

.facet + .facet {
  margin-top: 0.5rem;
  padding-top: 0.5rem;
  border-top: 1px solid var(--agwa-divider);
}

.facet h2 {
  margin: 0 0 0.15rem;
  font-size: inherit;
  font-weight: inherit;
}

.facet-toggle {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  width: 100%;
  padding: 0.1rem 0;
  font: inherit;
  font-size: 0.6875rem;
  font-weight: 600;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: var(--agwa-text-muted);
  background: none;
  border: 0;
  cursor: pointer;
  text-align: left;
}

.facet-toggle:hover {
  color: var(--agwa-text);
}

.facet-toggle:focus-visible {
  outline: 2px solid var(--agwa-accent);
  outline-offset: 2px;
  border-radius: 2px;
}

.caret {
  font-size: 0.625rem;
  color: var(--agwa-text-subtle);
}

.facet-title {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* How many values this group is applying. Present so collapsing a section can never hide a filter
   that is still narrowing the list. */
.applied {
  margin-left: auto;
  padding: 0 0.3rem;
  border-radius: var(--agwa-border-radius-pill);
  background: var(--agwa-accent-soft);
  color: var(--agwa-accent-text);
  font-variant-numeric: tabular-nums;
}

.panel[hidden] {
  display: none;
}

/* A facet declared unbounded (series / range grows with every register) is capped and scrolls
   inside itself, so it never grows the rail past the page. Catalogue facets are not capped: the
   rail as a whole scrolls when the page is short, and a fixed-size list has no reason to hide
   its tail behind a second scrollbar. */
.opts--capped {
  max-height: 13rem;
  overflow-y: auto;
}

/* The narrowing box for a long facet: as quiet as the options under it. */
.narrow {
  margin: 0.1rem 0 0.25rem;
  --sl-input-height-small: 1.5rem;
  --sl-input-font-size-small: 0.75rem;
}

/* The box has a real label for assistive technology; the facet heading directly above it is
   the visible label, so the control's own is clipped away rather than shown twice. */
.narrow::part(form-control-label) {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
}

.narrow::part(prefix) {
  font-size: 0.6875rem;
}

/* Why a facet is empty, or why the narrowing found nothing - one muted line in the space the
   options would take, so the rail keeps its shape. */
.empty-hint {
  margin: 0.15rem 0 0.25rem;
  font-size: 0.6875rem;
  line-height: 1.3;
  color: var(--agwa-text-subtle);
}

/* Top-aligned, not centred, because a label that wraps to two lines must keep its checkbox beside
   the FIRST line rather than floating to the middle of the pair. */
.opt {
  display: flex;
  align-items: flex-start;
  gap: 0.35rem;
  min-height: 1.5rem;
  padding: 0.1rem 0.15rem 0.1rem 0;
  border-radius: 3px;
  cursor: pointer;
}

.opt:hover {
  background: var(--agwa-surface-hover);
}

.opt sl-checkbox {
  flex: 1 1 auto;
  min-width: 0;
}

/* Wraps rather than truncates. A facet value is a name the reader has to RECOGNISE, and
   "Prime System Manufactur..." fails at exactly that - two lines cost a few pixels once, at load,
   and never move again. */
.opt sl-checkbox::part(label) {
  font-size: 0.75rem;
  line-height: 1.25;
  white-space: normal;
  overflow-wrap: break-word;
}

/* Counted with the option's own facet ignored, so ticking one value never renders its siblings as
   zero. Tabular figures keep the column steady as the counts change. */
.count {
  flex: 0 0 auto;
  font-size: 0.6875rem;
  color: var(--agwa-text-subtle);
  font-variant-numeric: tabular-nums;
}

/* Below the breakpoint the rail becomes a band above the list rather than a column beside it -
   at this width a column would leave the table too narrow to read. */
@media (max-width: 800px) {
  :host {
    flex-basis: auto;
    width: 100%;
    border-right: 0;
    border-bottom: 1px solid var(--agwa-border);
  }
}
